<template>
    <Loading v-if="loading" />

    <div v-else-if="errorMessage">
        {{errorMessage}}
    </div>

    <div v-else class="blogs">
        <ToastContainer />
        <div class="blogs__header">
            <h1 class="blogs__title">
                {{ blog.title }}
            </h1>
            <BlogCategories :categories="categories" :selected-category="selectedCategory" @category-selected="onCategorySelected" />
        </div>
        <Loading v-if="loading" />
        <div v-else-if="displayedPosts.length" class="blogs__body">
            <div class="blogs__list">
                <BlogPost v-for="post in displayedPosts" :key="post.id" class="blogs__list-item" :post />
            </div>
        </div>
        <div class="blogs__footer">
            <button v-if="hasMorePosts" ref="viewMoreButton" @click="loadMorePosts" class="blog__view-more"
                    :aria-busy="false">
                View more
            </button>
        </div>
    </div>
</template>

<script setup>
import BlogPost from './BlogPostItem.vue';
import BlogCategories from './BlogCategories.vue';
import ToastContainer from '../Toast/ToastContainer.vue';

import {computed, onBeforeMount, ref} from 'vue';
import {getBlog} from "../services/get-blogs.mjs";
import {useToast} from '../composables/toaster.mjs';

const props = defineProps({
    blogId: {
        type: String,
        required: true
    },
    paginationLimit: {
        type: Number,
        required: false
}
});

const { add: addToast } = useToast();
const categories = ref([]);
const selectedCategory = ref(null);
const loading = ref(true);
const blog = ref({});
const errorMessage = ref(null);
const displayedPosts = ref([]);

const limit = (props.paginationLimit) ? Number(props.paginationLimit) : 9;
const displayedPostsCount = ref(limit);
const totalPostsCount = ref(0);

const extractCategories = () => {
    const posts = blog.value.posts.docs;
    if (posts.length < 1) {
        categories.value = [];
        return;
    }

    const categorySet = new Map();
    posts.forEach(post => {
        if (post.categories && Array.isArray(post.categories)) {
            post.categories.forEach(category => {
                if (!categorySet.has(category.id)) {
                    categorySet.set(category.id, category);
                }
            });
        }
    });

    const allArticlesCategory = { id: 'all', title: 'All articles', slug: 'all' };
    categories.value = [
        allArticlesCategory,
        ...Array.from(categorySet.values())
    ];

    selectedCategory.value = allArticlesCategory;
    loadPostsByCategory(allArticlesCategory);
};

const loadPostsByCategory = (selectedCategory) => {
    let filteredPosts = 0;

    if (!selectedCategory) return;

    const posts = blog.value.posts.docs;

    if (selectedCategory.id === 'all') {
        filteredPosts = posts || [];
    } else {
        filteredPosts = posts.filter(post =>
            post.categories?.some(postCategory => postCategory.id === selectedCategory.id)
        ) || [];
    }

    const startIndex = 0;

    totalPostsCount.value = filteredPosts.length;

    displayedPosts.value = filteredPosts.filter((_, index) => index >= startIndex && index < displayedPostsCount.value);
};

const hasMorePosts = computed(() => {
    return displayedPostsCount.value < totalPostsCount.value;
});

const loadMorePosts = () => {
    displayedPostsCount.value += limit;
    loadPostsByCategory(selectedCategory.value);
    addToast(`Showing ${displayedPostsCount.value} of ${totalPostsCount} articles`);
};

const onCategorySelected = (category) => {
    displayedPostsCount.value = limit;
    selectedCategory.value = category;
    loadPostsByCategory(category);
    addToast(`Showing ${category.title} articles`);
};

onBeforeMount(async () => {
    try {
        blog.value = await getBlog(props.blogId);

        extractCategories();

    } catch (error) {
        console.error(error);
        errorMessage.value = 'Something has gone wrong, please try again later.';
        blog.value = {};
    } finally {
        loading.value = false;
    }
});
</script>
